export interface GuideSection {
    heading: string
    paragraphs?: string[]
    bullets?: string[]
}

export interface GuideInfo {
    slug: string
    title: string
    summary: string
    reviewed: string
    intro: string
    sections: GuideSection[]
    closing?: string
    links: { label: string, href: string }[]
}

export const GUIDES: GuideInfo[] = [
    {
        slug: 'image-compression-guide',
        title: 'Image compression without the guesswork',
        summary: 'Understand how compression, pixel dimensions and file formats affect KB and MB limits and visible quality.',
        reviewed: 'September 20, 2026',
        intro: 'Compression reduces the number of bytes an image uses. Resizing is a different step. A large photo can be compressed without changing its dimensions, and a resized photo can still be a big file. Keeping the two ideas apart makes most upload problems much easier to solve.',
        sections: [
            {
                heading: 'Start with the requirement you actually have',
                paragraphs: [
                    'A limit such as 500KB is about file size. A requirement such as 1080 × 1080 is about dimensions, the number of pixels across and down.',
                    'If a site asks for both, resize or crop first and compress second. That way no bytes are spent on pixels that will never be shown.'
                ]
            },
            {
                heading: 'Why photos and graphics compress differently',
                bullets: [
                    'Photos have smooth tones and irregular detail. Lossy formats such as JPG and WebP handle them well.',
                    'Logos, screenshots and diagrams have flat color, sharp edges and text. PNG keeps them crisp, but the files can be larger.'
                ]
            },
            {
                heading: 'What happens when you push the target too low',
                paragraphs: [
                    'Heavy compression can cause blocky patches, halos around edges, smeared texture and blurry text.',
                    'Aim for the smallest file that still looks acceptable where it will be used, not the smallest number possible. Judge the result at the size people will actually see it. Zooming far in exaggerates flaws, and tiny thumbnails can hide real damage.'
                ]
            },
            {
                heading: '100KB, 200KB, 500KB or 1MB?',
                paragraphs: [
                    'Small targets work for strict forms and thumbnails. Larger targets leave room for detailed photos.',
                    'No file size guarantees a particular quality. Two images with the same dimensions can hold very different amounts of detail. A 4000 × 3000 photo of a busy street needs far more bytes than a 1200 × 900 photo of a plain wall.'
                ]
            },
            {
                heading: 'Keep the original',
                paragraphs: [
                    'Treat the compressed file as a delivery copy and keep the original. You can then make a new copy later for print, another platform or a looser limit.'
                ]
            }
        ],
        links: [
            { label: 'Open the image compressor', href: '/compress-image' },
            { label: 'Compress to 100KB', href: '/compress-image?size=100&unit=KB' },
            { label: 'Compress to 500KB', href: '/compress-image?size=500&unit=KB' },
            { label: 'Resize first', href: '/resize-image' }
        ]
    },
    {
        slug: 'image-resizing-guide',
        title: 'Resize images without stretching them',
        summary: 'Learn when to resize, crop or keep the aspect ratio, and how common sizes such as 1080 × 1080 and 1920 × 1080 differ.',
        reviewed: 'September 20, 2026',
        intro: "Resizing changes an image's pixel dimensions. The real decision is what to do when the size you need is a different shape from the image you have.",
        sections: [
            {
                heading: 'Aspect ratio explains the shape',
                paragraphs: [
                    'Aspect ratio is the relationship between width and height. 1920 × 1080 is 16:9, 1080 × 1920 is 9:16, and 1080 × 1080 is 1:1. Images with very different pixel counts can share the same ratio.'
                ]
            },
            {
                heading: 'Keep the shape, crop or stretch?',
                bullets: [
                    'Keeping the shape changes the size but not the proportions.',
                    'Cropping trims part of the picture so it fits a new shape.',
                    'Stretching forces the new dimensions and can warp circles, faces, logos and text.'
                ],
                paragraphs: [
                    'For most photos, keep the shape unless the destination needs a specific one. If it does, crop on purpose rather than stretching.'
                ]
            },
            {
                heading: 'Making smaller versus making bigger',
                paragraphs: [
                    'Shrinking throws pixels away, but the result usually looks fine because it is shown smaller.',
                    'Enlarging makes the software invent in-between pixels. The file may get bigger, but it cannot gain real detail that was never in the original.'
                ]
            },
            {
                heading: 'Common sizes are not universal rules',
                paragraphs: [
                    '1080 × 1080 is a handy square, 1080 × 1920 is a common vertical canvas, and 1920 × 1080 is a common landscape one. Sites and apps change their recommendations, so always check what the destination asks for.'
                ]
            },
            {
                heading: 'File size comes afterward',
                paragraphs: [
                    'Correct dimensions do not guarantee a file meets a KB or MB limit. Compress the image after resizing if the file is still too heavy.'
                ]
            }
        ],
        links: [
            { label: 'Open the image resizer', href: '/resize-image' },
            { label: 'Aspect ratio calculator', href: '/aspect-ratio' },
            { label: 'Crop image', href: '/crop-image' },
            { label: 'Compress the result', href: '/compress-image' }
        ]
    },
    {
        slug: 'image-format-guide',
        title: 'JPG, PNG or WebP? Choose by content and destination',
        summary: 'Compare JPG, PNG and WebP for photos, transparency, screenshots, compatibility and websites.',
        reviewed: 'September 20, 2026',
        intro: 'Image formats store pixels in different ways and support different features. Matching the format to the picture and to where it is going can improve quality, compatibility and file size.',
        sections: [
            {
                heading: 'JPG: practical for photos',
                bullets: [
                    'Lossy compression that almost everything can open.',
                    'A sensible default for photos that do not need transparency.',
                    'Saving the same JPG again and again slowly lowers its quality, so keep an original of important images.'
                ]
            },
            {
                heading: 'PNG: transparency and crisp graphics',
                bullets: [
                    'Lossless, and supports transparent backgrounds.',
                    'A good fit for logos, app screenshots, diagrams and graphics with sharp text.',
                    'Photos saved as PNG can be many times larger than the same photo as JPG or WebP.'
                ]
            },
            {
                heading: 'WebP: efficient for websites',
                bullets: [
                    'Has both lossy and lossless modes and supports transparency.',
                    'Popular online because it often makes smaller files.',
                    'Some older software and upload forms still expect JPG or PNG, so compatibility may decide for you.'
                ]
            },
            {
                heading: 'Converting cannot bring back missing detail',
                paragraphs: [
                    'Converting a JPG to PNG does not undo the earlier compression. The PNG keeps exactly the pixels it was given without losing more, but detail the JPG already threw away does not return.'
                ]
            },
            {
                heading: 'Transparency needs a decision',
                paragraphs: [
                    'JPG has no transparency. When you convert a transparent PNG or WebP to JPG, the empty areas must be filled with a solid background color.'
                ]
            }
        ],
        closing: 'Choose the format for what happens to the file next, rather than assuming one format is always higher quality.',
        links: [
            { label: 'Open the format converter', href: '/convert-image' },
            { label: 'PNG to JPG', href: '/convert-image?to=jpg' },
            { label: 'JPG to PNG', href: '/convert-image?to=png' },
            { label: 'JPG to WebP', href: '/convert-image?to=webp' }
        ]
    },
    {
        slug: 'image-metadata-privacy-guide',
        title: 'What can be hidden inside an image file?',
        summary: 'Learn what EXIF and other image metadata can contain, what removing it changes, and what to check before sharing a photo.',
        reviewed: 'September 20, 2026',
        intro: 'Besides the picture you can see, an image file can record details about how it was made. Depending on the device and format, that can include the camera model, settings, editing software, date and time, orientation and sometimes GPS coordinates.',
        sections: [
            {
                heading: 'EXIF is only one kind of metadata',
                paragraphs: [
                    'EXIF is the type most people know from digital photos, but a file can hold several kinds of stored details. Removing them gives you a cleaner copy to share. It does not change what the picture shows.'
                ]
            },
            {
                heading: 'Removing metadata does not hide what is visible',
                paragraphs: [
                    'An address, a number plate, a name badge or a private message that appears in the picture is still there after the hidden details are removed. Cleaning metadata and protecting what is visible are two separate jobs.'
                ]
            },
            {
                heading: 'Why location deserves attention',
                paragraphs: [
                    'Photos taken with location turned on can contain GPS coordinates. Removing them before posting publicly or sharing with strangers helps prevent giving away where a photo was taken.'
                ]
            },
            {
                heading: 'Keep the original when the details matter',
                paragraphs: [
                    'Camera settings and dates can be useful for photography, records, warranty claims or personal archives. Make a separate clean copy for sharing instead of replacing your only original.'
                ]
            },
            {
                heading: 'Check the result',
                paragraphs: [
                    "If the information is sensitive, open the clean copy's file properties on your computer or in another trusted viewer and confirm. No privacy step should rely on assumption alone."
                ]
            }
        ],
        links: [
            { label: 'Remove image metadata', href: '/remove-image-metadata' },
            { label: 'Crop out visible details', href: '/crop-image' },
            { label: 'Privacy policy', href: '/privacy' }
        ]
    },
    {
        slug: 'images-to-pdf-guide',
        title: 'Turn a stack of images into a PDF people can actually use',
        summary: 'Prepare scans and photos before combining them, so the PDF is clean, readable and easy to send.',
        reviewed: 'September 20, 2026',
        intro: 'Combining images is easy. Preparing them first is what makes the result feel like one document instead of a pile of photos. Order, rotation, readability and file size all matter.',
        sections: [
            {
                heading: '1. Fix the orientation first',
                paragraphs: [
                    'Make sure every page has its text upright. One sideways page forces readers to rotate their whole viewer, and consistent pages print more reliably.'
                ]
            },
            {
                heading: '2. Put the pages in order',
                paragraphs: [
                    'Rename or sort the files so it is obvious which is page one, page two and any attachments. The Photos to PDF tool lets you reorder pages, but a tidy starting order prevents mistakes.'
                ]
            },
            {
                heading: '3. Crop away the background',
                paragraphs: [
                    'For photos of paper documents, trim desks, shadows and wide margins where you can. The pages become easier to read and the file can get smaller.'
                ]
            },
            {
                heading: '4. Balance readability and file size',
                paragraphs: [
                    'Text must stay sharp enough to read, but phone photos are often far larger than a PDF needs. If the PDF is too big to upload or email, choose a smaller file setting or resize copies of the images first.'
                ]
            },
            {
                heading: '5. Check the finished PDF page by page',
                paragraphs: [
                    'Open the saved file and check the order, rotation and readability. Make sure no page is missing, repeated or cut off before you share it.'
                ]
            }
        ],
        links: [
            { label: 'Open Photos to PDF', href: '/images-to-pdf' },
            { label: 'Crop image', href: '/crop-image' },
            { label: 'Resize image', href: '/resize-image' },
            { label: 'Compress image', href: '/compress-image' }
        ]
    },
    {
        slug: 'pdf-workflow-guide',
        title: 'A safer way to edit and finish a PDF',
        summary: 'A practical checklist for editing, signing, organizing and reviewing a PDF before you share it.',
        reviewed: 'October 11, 2026',
        intro: 'The exact tools change from document to document, but working in a steady order makes mistakes much easier to catch.',
        sections: [
            {
                heading: '1. Keep the original',
                paragraphs: [
                    'Save an untouched copy of any important file before you start. If the layout, page order or a later export looks wrong, you have something to compare against.'
                ]
            },
            {
                heading: '2. Change the content before finishing the pages',
                paragraphs: [
                    'Edit text, add text and insert images before adding page numbers, headers or watermarks. Finishing touches are easier to check once the content is final.'
                ]
            },
            {
                heading: '3. Organize the pages',
                bullets: [
                    'Rotate, reorder, duplicate or remove pages as needed.',
                    'When merging files, check where each document starts and ends.',
                    'When splitting or extracting, open the new file and confirm the right pages came through.'
                ]
            },
            {
                heading: '4. Add the signature and date on purpose',
                paragraphs: [
                    'Sign only when the document is complete. In DOCSCRUB, add the date with the Add Text tool. Check placement and size at a comfortable zoom, especially when the signature has to fit a line or box.'
                ]
            },
            {
                heading: '5. Apply finishing touches last',
                paragraphs: [
                    'Add watermarks, page numbers, headers and footers once the page order is locked, so nothing ends up on the wrong page.'
                ]
            },
            {
                heading: '6. Review the saved file, not just the editor',
                paragraphs: [
                    'The saved PDF is what other people will receive. Reopen it, go through every page, zoom in on anything you edited, and confirm the signature, date, page order and finishing touches all appear.'
                ]
            }
        ],
        links: [
            { label: 'DOCSCRUB details', href: '/docscrub' },
            { label: 'Open DOCSCRUB', href: 'https://app.rarefrequencyworks.com/' },
            { label: 'DOCSCRUB support', href: '/support' }
        ]
    }
]

export const findGuide = (slug: string): GuideInfo | undefined => GUIDES.find((guide) => guide.slug === slug)
